/* eslint-disable lines-between-class-members */
import {
  ScriptableObject,
  ScriptableObjectMixin,
  ScriptableObjects
} from "./Base.js";

const TRIGGER_PROPS = [
  "target",
  "enterComponent",
  "enterProperty",
  "enterValue",
  "leaveComponent",
  "leaveProperty",
  "leaveValue"
];

// Utility function to walk up prototype chain and find property descriptors
function findPropertyDescriptor(obj, prop) {
  let current = obj;
  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, prop);
    if (descriptor) return descriptor;
    current = Object.getPrototypeOf(current);
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Editor adapter                                                             */
/* -------------------------------------------------------------------------- */

class TriggerVolumeEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._originalDescriptors = originalDescriptors;
    this._originalSetters = new Map();

    TRIGGER_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => {
          const descriptor = this._originalDescriptors.get(prop);
          if (descriptor && descriptor.get) {
            return descriptor.get.call(this.sourceObject);
          } else if (descriptor && descriptor.value !== undefined) {
            return descriptor.value;
          } else {
            // For simple properties, try to read directly from the object
            const value = Object.getOwnPropertyDescriptor(
              this.sourceObject,
              prop
            )?.value;
            return value !== undefined
              ? value
              : this.sourceObject?.[`_original_${prop}`];
          }
        },
        set: v => {
          if (!this.sourceObject) return;

          const descriptor = this._originalDescriptors.get(prop);
          if (descriptor && descriptor.set) {
            descriptor.set.call(this.sourceObject, v);
          } else {
            // Use stored original setter to bypass our proxy
            const originalSetter = this._originalSetters.get(prop);
            if (originalSetter) {
              try {
                originalSetter(v);
              } catch (e) {
                console.warn(`Failed to call original setter for ${prop}:`, e);
                this.sourceObject[`_original_${prop}`] = v;
              }
            } else {
              // Fallback: store as backup value or set directly
              try {
                this.sourceObject[prop] = v;
              } catch (e) {
                this.sourceObject[`_original_${prop}`] = v;
                console.warn(
                  `No original setter found for ${prop}, storing as backup`
                );
              }
            }
          }
        }
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  A-Frame adapter                                                             */
/* -------------------------------------------------------------------------- */

class TriggerVolumeAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "trigger-volume";

    const setCompProp = (key, val) => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(COMPONENT) || {})
      };
      current[key] = val;
      this.sourceObject.el.setAttribute(COMPONENT, current);
    };

    const getCompProp = key =>
      this.sourceObject?.el?.getAttribute(COMPONENT)?.[key];

    TRIGGER_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getCompProp(prop),
        set: v => setCompProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Factory functions                                                          */
/* -------------------------------------------------------------------------- */

function createTriggerVolumeElementAFrame(initial = {}) {
  if (typeof document === "undefined") {
    console.error(
      "Cannot create TriggerVolumeElementAFrame in non-browser environment"
    );
    return null;
  }

  const el = document.createElement("a-entity");
  // Default trigger-volume component reflecting node defaults and unit size.
  el.setAttribute("trigger-volume", {
    size: { x: 1, y: 1, z: 1 },
    target: null,
    enterComponent: null,
    enterProperty: null,
    enterValue: null,
    leaveComponent: null,
    leaveProperty: null,
    leaveValue: null,
    ...initial
  });

  // Create the scriptable object that represents this A-Frame entity
  const ScriptableTriggerVolumeClass = ScriptableObjectMixin(
    ScriptableObject,
    "TriggerVolumeElement"
  );

  const triggerVolumeObj = new ScriptableTriggerVolumeClass();

  // Set A-Frame specific properties
  triggerVolumeObj.el = el;
  triggerVolumeObj.scriptableType = "trigger-volume";
  triggerVolumeObj._adapter = new TriggerVolumeAFrameAdapter(triggerVolumeObj);

  // Apply initial properties (skip already applied in setAttribute)
  const propsToApply = { ...initial };
  Object.keys(propsToApply).forEach(key => {
    if (key in triggerVolumeObj && !TRIGGER_PROPS.includes(key)) {
      triggerVolumeObj[key] = propsToApply[key];
    }
  });

  return triggerVolumeObj;
}

function createTriggerVolumeElementEditor(initial = {}) {
  /* eslint-disable no-undef */
  const NodeCtor =
    (typeof globalThis !== "undefined" &&
      (globalThis.TriggerVolumeNode || globalThis.Object3D)) ||
    undefined;

  if (typeof NodeCtor !== "function") {
    throw new Error(
      "TriggerVolumeElementEditor: TriggerVolumeNode constructor not found – cannot create element in editor runtime."
    );
  }

  const editor = ScriptableObject.editor;
  if (!editor) {
    throw new Error(
      "TriggerVolumeElementEditor: ScriptableObject.editor not available – cannot create element in editor runtime."
    );
  }

  let node;
  try {
    node = new NodeCtor(editor);
  } catch (e) {
    throw new Error(
      `TriggerVolumeElementEditor: failed to construct TriggerVolumeNode – ${e?.message ||
        e}`
    );
  }

  // IMPORTANT: Capture original descriptors BEFORE we override them
  const originalDescriptors = new Map();
  const originalSetters = new Map();

  TRIGGER_PROPS.forEach(prop => {
    const descriptor = findPropertyDescriptor(node, prop);
    if (descriptor) {
      originalDescriptors.set(prop, descriptor);

      // Store original setter function for direct access
      if (descriptor.set) {
        originalSetters.set(prop, descriptor.set.bind(node));
      } else if (descriptor.writable !== false) {
        // Create a setter function for writable properties
        originalSetters.set(
          prop,
          function(value) {
            Object.defineProperty(this, prop, {
              value: value,
              writable: true,
              configurable: true,
              enumerable: true
            });
          }.bind(node)
        );
      }
    }
  });

  // Set scriptable type and create adapter with original descriptors
  node.scriptableType = "trigger-volume";
  node._adapter = new TriggerVolumeEditorAdapter(node, originalDescriptors);

  // Store original setters on the adapter for direct access
  node._adapter._originalSetters = originalSetters;

  // Set up property proxying for TRIGGER_PROPS to use the adapter
  TRIGGER_PROPS.forEach(prop => {
    Object.defineProperty(node, prop, {
      get() {
        return this._adapter?.[prop];
      },
      set(v) {
        if (this._adapter) {
          this._adapter[prop] = v;
        }
      },
      enumerable: true,
      configurable: true
    });
  });

  // Add scriptable object marker
  node.isScriptableObject = true;

  // Apply initial properties
  Object.keys(initial).forEach(key => {
    if (key in node) node[key] = initial[key];
  });

  return node;
}

/* -------------------------------------------------------------------------- */
/*  TriggerVolumeScriptableObject                                              */
/* -------------------------------------------------------------------------- */

export class TriggerVolumeScriptableObject extends ScriptableObject {
  constructor(obj) {
    super(obj);
    if (ScriptableObject.runtimeMode === "aframe" || obj?.el) {
      this._adapter = new TriggerVolumeAFrameAdapter(this._sourceObject);
    } else {
      this._adapter = new TriggerVolumeEditorAdapter(this._sourceObject);
    }

    if (this._sourceObject) {
      this.visible = this._sourceObject.visible;
    }
  }

  static _trackedProps = TRIGGER_PROPS;

  static _ensureProxy() {
    if (this._propsProxied) return;
    this._propsProxied = true;
    for (const prop of this._trackedProps) {
      if (prop in this.prototype) continue;
      Object.defineProperty(this.prototype, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        }
      });
    }
  }

  dispose() {
    if (this._adapter?.dispose) {
      this._adapter.dispose();
    }
    if (this.parent) {
      this.parent.remove(this);
    }
  }
}

TriggerVolumeScriptableObject._ensureProxy();

/* -------------------------------------------------------------------------- */
/*  TriggerVolumeElement - Main factory class                                 */
/* -------------------------------------------------------------------------- */

export class TriggerVolumeElement {
  constructor(initial = {}) {
    const runtimeMode = ScriptableObject.runtimeMode || "editor";

    if (runtimeMode === "aframe") {
      return createTriggerVolumeElementAFrame(initial);
    } else {
      return createTriggerVolumeElementEditor(initial);
    }
  }
}

// Register factory so scene.getObjectByName wraps automatically
ScriptableObjects.registerType(
  "trigger-volume",
  obj => new TriggerVolumeScriptableObject(obj)
);

export {
  TriggerVolumeAFrameAdapter,
  TriggerVolumeEditorAdapter,
  TRIGGER_PROPS
};
